/**
 * AddressSection — physical + mailing address capture with USPS validation.
 *
 * Ported from legacy SPFx `AddressSection.tsx` (shared/cif/AddressSection),
 * extended per docs/port-design/people-form-port.md §2 (address pipeline),
 * §3 (ZIP+4 strip toggle), and §4 (Core Director storage inversion is handled
 * upstream in useInquiryHydration / useSaveDispatch — this component only
 * edits the UI-visible `addr / mailAddr` pair).
 *
 * Pipeline invariant (§2):
 *   AI -> postProcessAddress -> USPS -> titleCase -> review dialog
 *
 * USPS calls return ALL CAPS; `titleCase()` is applied to the street + city
 * before they land in RaoPerson. State codes are preserved uppercase. ZIP+4 is
 * stripped when `systemSettings['address.stripZipPlus4']` is truthy (default
 * true per §3). Lookup is admin-editable — we read it off the ConfigContext
 * bundle instead of hard-coding.
 *
 * Change-highlighting:
 *   If `isChanged(fieldName)` returns true, the input renders with a blue
 *   left border + light-blue background (brand token conventions). This
 *   matches the SPFx review dialog's "what did the officer touch" affordance.
 */
import { useCallback, useEffect, useMemo, useRef, useState, type CSSProperties } from 'react';
import type { UspsAddressResponse } from '@retail-ops/shared-types';
import { Button } from '@/components/primitives';
import { useUsps, DPV_LABELS } from '@/hooks/useUsps';
import { useConfig } from '@/contexts/ConfigContext';
import { useOptionalCart } from '@/contexts/CartContext';
import type { RaoPerson } from '../../../types';
import { parsePeople } from '../../../utils/cartStateParse';
import { titleCase } from '../../../utils/titleCase';
import { stripZipPlus4Enabled, uspsZip } from '@/utils/zipPlus4';
import { joinStreetLines, normalizeStreet } from './streetLine';
import { UspsSuggestionPanel } from '@/components/address/UspsSuggestionPanel';

// -----------------------------------------------------------------------------
// Public props
// -----------------------------------------------------------------------------

export interface AddressSectionProps {
  person: RaoPerson;
  onChange: (patch: Partial<RaoPerson>) => void;
  /** Called with a field name — returns true to render the "changed" style. */
  isChanged?: (field: string) => boolean;
  /** Fully locks the section (no edits, no USPS calls). */
  readOnly?: boolean;
}

// -----------------------------------------------------------------------------
// State dropdown options — 50 + DC + 5 US territories
// -----------------------------------------------------------------------------

const US_STATES: ReadonlyArray<{ code: string; name: string }> = [
  { code: 'AL', name: 'Alabama' }, { code: 'AK', name: 'Alaska' },
  { code: 'AZ', name: 'Arizona' }, { code: 'AR', name: 'Arkansas' },
  { code: 'CA', name: 'California' }, { code: 'CO', name: 'Colorado' },
  { code: 'CT', name: 'Connecticut' }, { code: 'DE', name: 'Delaware' },
  { code: 'FL', name: 'Florida' }, { code: 'GA', name: 'Georgia' },
  { code: 'HI', name: 'Hawaii' }, { code: 'ID', name: 'Idaho' },
  { code: 'IL', name: 'Illinois' }, { code: 'IN', name: 'Indiana' },
  { code: 'IA', name: 'Iowa' }, { code: 'KS', name: 'Kansas' },
  { code: 'KY', name: 'Kentucky' }, { code: 'LA', name: 'Louisiana' },
  { code: 'ME', name: 'Maine' }, { code: 'MD', name: 'Maryland' },
  { code: 'MA', name: 'Massachusetts' }, { code: 'MI', name: 'Michigan' },
  { code: 'MN', name: 'Minnesota' }, { code: 'MS', name: 'Mississippi' },
  { code: 'MO', name: 'Missouri' }, { code: 'MT', name: 'Montana' },
  { code: 'NE', name: 'Nebraska' }, { code: 'NV', name: 'Nevada' },
  { code: 'NH', name: 'New Hampshire' }, { code: 'NJ', name: 'New Jersey' },
  { code: 'NM', name: 'New Mexico' }, { code: 'NY', name: 'New York' },
  { code: 'NC', name: 'North Carolina' }, { code: 'ND', name: 'North Dakota' },
  { code: 'OH', name: 'Ohio' }, { code: 'OK', name: 'Oklahoma' },
  { code: 'OR', name: 'Oregon' }, { code: 'PA', name: 'Pennsylvania' },
  { code: 'RI', name: 'Rhode Island' }, { code: 'SC', name: 'South Carolina' },
  { code: 'SD', name: 'South Dakota' }, { code: 'TN', name: 'Tennessee' },
  { code: 'TX', name: 'Texas' }, { code: 'UT', name: 'Utah' },
  { code: 'VT', name: 'Vermont' }, { code: 'VA', name: 'Virginia' },
  { code: 'WA', name: 'Washington' }, { code: 'WV', name: 'West Virginia' },
  { code: 'WI', name: 'Wisconsin' }, { code: 'WY', name: 'Wyoming' },
  { code: 'DC', name: 'District of Columbia' },
  { code: 'AS', name: 'American Samoa' }, { code: 'GU', name: 'Guam' },
  { code: 'MP', name: 'Northern Mariana Islands' },
  { code: 'PR', name: 'Puerto Rico' }, { code: 'VI', name: 'US Virgin Islands' },
];

// -----------------------------------------------------------------------------
// Field styles
// -----------------------------------------------------------------------------

const labelStyle: CSSProperties = {
  display: 'block',
  fontSize: 11,
  color: 'var(--ink-600)',
  textTransform: 'uppercase',
  letterSpacing: 0.4,
  fontWeight: 600,
  marginBottom: 3,
};

const inputStyleBase: CSSProperties = {
  width: '100%',
  padding: '8px 10px',
  border: '1px solid var(--ink-200)',
  borderRadius: 6,
  fontSize: 13,
  boxSizing: 'border-box',
};

const inputStyleChanged: CSSProperties = {
  ...inputStyleBase,
  borderLeft: '3px solid var(--pcb-blue)',
  background: 'var(--field-changed-bg, #f0f7ff)',
  color: 'var(--ink-900, #1d1d1f)',
};

const readOnlyInputStyle: CSSProperties = {
  ...inputStyleBase,
  background: 'var(--ink-50, #f5f5f5)',
  color: 'var(--ink-700)',
};

const sectionHeaderStyle: CSSProperties = {
  fontSize: 10,
  fontWeight: 600,
  textTransform: 'uppercase',
  letterSpacing: 0.8,
  color: 'var(--pcb-blue)',
  marginBottom: 8,
};

// -----------------------------------------------------------------------------
// Read the ZIP+4 admin toggle off the ConfigContext
// -----------------------------------------------------------------------------

function readStripZipPlus4(systemSettings: Record<string, unknown> | undefined): boolean {
  // Shared ZIP+4 policy (utils/zipPlus4, BACKLOG-1292): strip unless turned off.
  return stripZipPlus4Enabled(systemSettings?.['address.stripZipPlus4']);
}

// -----------------------------------------------------------------------------
// Apply the post-USPS title-case pass (skips state code)
// -----------------------------------------------------------------------------

interface CanonicalAddress {
  streetAddress: string;
  /** USPS secondary address (apartment / suite / unit), e.g. "APT C" — title-cased. */
  secondary: string;
  city: string;
  state: string;
  zip: string;
}

function toCanonical(resp: UspsAddressResponse, stripZipPlus4: boolean): CanonicalAddress {
  const addrBlock =
    (resp as { address?: { streetAddress?: string; city?: string; state?: string; ZIPCode?: string; ZIPPlus4?: string } }).address ??
    (resp as unknown as { streetAddress?: string; city?: string; state?: string; ZIPCode?: string; ZIPPlus4?: string });
  const street = typeof addrBlock?.streetAddress === 'string' ? addrBlock.streetAddress : '';
  // USPS Addresses 3.0 returns the unit separately (address.secondaryAddress). Dropping it
  // made "Accept Standardized" strip "Apt C" and could leave an undeliverable address
  // (Bill 2026-10-06).
  const secondary =
    typeof (addrBlock as { secondaryAddress?: string })?.secondaryAddress === 'string'
      ? ((addrBlock as { secondaryAddress?: string }).secondaryAddress ?? '').trim()
      : '';
  const city = typeof addrBlock?.city === 'string' ? addrBlock.city : '';
  const state = typeof addrBlock?.state === 'string' ? addrBlock.state : '';
  const zip5 = typeof addrBlock?.ZIPCode === 'string' ? addrBlock.ZIPCode : '';
  const zip4 = typeof addrBlock?.ZIPPlus4 === 'string' ? addrBlock.ZIPPlus4 : '';

  const zip = uspsZip(zip5, zip4, stripZipPlus4);
  return {
    streetAddress: titleCase(street),
    secondary: titleCase(secondary),
    city: titleCase(city),
    state: state.toUpperCase(),
    zip,
  };
}

/** The unit designator in a street line ("Apt C", "Ste 200", "Unit 4B", "# 12", "Lot 7"), or ''. */
const UNIT_RE = /\b(?:apt|apartment|unit|ste|suite|lot|trlr|trailer|bldg|building|fl|floor|rm|room|spc|space|dept|ofc|office|ph|penthouse)\b\.?\s*#?\s*[A-Za-z0-9-]+|#\s*[A-Za-z0-9-]+/i;
export function unitOf(line: string | undefined | null): string {
  const m = (line ?? '').match(UNIT_RE);
  return m ? m[0].trim() : '';
}

/**
 * Street line to save after USPS standardization — ONE delivery line (Bill 2026-10-06).
 * Never loses a unit: USPS's secondary goes back on, and if USPS returned no unit but the
 * entered address had one (on the street or an older street line 2), the entered unit is
 * kept. A street line 2 is folded in and cleared.
 */
export function standardizedStreet(
  canon: { streetAddress: string; secondary: string },
  entered: { addr?: string | null; addr2?: string | null },
): { addr: string; addr2?: string } {
  const enteredUnit = unitOf(entered.addr2) || unitOf(entered.addr) || (entered.addr2 ?? '').trim();
  const unit = canon.secondary || (unitOf(canon.streetAddress) ? '' : enteredUnit);
  const addr = unit && !unitOf(canon.streetAddress) ? `${canon.streetAddress} ${unit}` : canon.streetAddress;
  return entered.addr2 && entered.addr2.trim() ? { addr, addr2: '' } : { addr };
}

/** True when two street lines are the same delivery line (case, punctuation and USPS abbreviations ignored). */
export function sameStreet(a: string | null | undefined, b: string | null | undefined): boolean {
  return normalizeStreet(a) === normalizeStreet(b);
}

// -----------------------------------------------------------------------------
// AddressSection component
// -----------------------------------------------------------------------------

export function AddressSection({
  person,
  onChange,
  isChanged,
  readOnly = false,
}: AddressSectionProps): JSX.Element {
  const usps = useUsps();
  const { config } = useConfig();

  // "Same address as …" (Bill 2026-10-14): for a second, third … person, copy the physical and
  // mailing address of someone already on this cart in one click. Only people with a street
  // address are offered; never the person being edited.
  const cartCtx = useOptionalCart();
  const addressDonors = useMemo(() => {
    const cart = cartCtx?.cart;
    if (!cart) return [] as ReadonlyArray<RaoPerson>;
    return parsePeople(cart).filter((p) =>
      (p.addr ?? '').trim().length > 0 && (p.id ?? '') !== (person.id ?? '\u0000'));
  }, [cartCtx?.cart, person.id]);
  const [copiedFrom, setCopiedFrom] = useState<string | null>(null);
  const copyAddressFrom = useCallback((donorId: string): void => {
    const d = addressDonors.find((p) => p.id === donorId);
    if (!d) return;
    const donorExtra = (d.extra ?? {}) as Record<string, unknown>;
    const useMail = typeof donorExtra['useMailingAddr'] === 'boolean' ? (donorExtra['useMailingAddr'] as boolean) : false;
    onChange({
      addr: d.addr ?? '', addr2: d.addr2 ?? '', city: d.city ?? '', state: d.state ?? '', zip: d.zip ?? '',
      mailAddr: d.mailAddr ?? '', mailCity: d.mailCity ?? '', mailState: d.mailState ?? '', mailZip: d.mailZip ?? '',
      extra: { ...((person.extra ?? {}) as Record<string, unknown>), useMailingAddr: useMail },
    });
    setCopiedFrom(d.fullName || [d.firstName, d.lastName].filter(Boolean).join(' ') || 'the other person');
  }, [addressDonors, onChange, person.extra]);
  const stripZipPlus4 = useMemo(
    () => readStripZipPlus4(config.systemSettings as Record<string, unknown> | undefined),
    [config.systemSettings],
  );

  const lastZipRef = useRef<string>('');
  // Latest city/state for the async ZIP lookup (it resolves after later keystrokes).
  const cityRef = useRef<string | undefined>(person.city);
  const stateRef = useRef<string | undefined>(person.state);
  cityRef.current = person.city;
  stateRef.current = person.state;

  // `useMailingAddr` is not yet on the RaoPerson type — stored under `extra`
  // so the Wave-3 promotion to shared-types doesn't cart-migrate anything.
  const extraUseMailing = person.extra && typeof (person.extra as Record<string, unknown>)['useMailingAddr'] === 'boolean'
    ? ((person.extra as Record<string, unknown>)['useMailingAddr'] as boolean)
    : false;
  const [useMailingAddr, setUseMailingAddr] = useState<boolean>(extraUseMailing);

  useEffect(() => {
    // Keep local toggle in sync when person is re-hydrated externally.
    setUseMailingAddr(extraUseMailing);
  }, [extraUseMailing]);

  // Clear any stored USPS result whenever the physical address changes —
  // stale DPV badges after an edit are misleading.
  useEffect(() => {
    usps.reset();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [person.addr, person.city, person.state, person.zip]);

  const changedStyle = useCallback(
    (field: string): CSSProperties => {
      if (readOnly) return readOnlyInputStyle;
      if (isChanged && isChanged(field)) return inputStyleChanged;
      return inputStyleBase;
    },
    [isChanged, readOnly],
  );

  // ---------------------------------------------------------------------------
  // ZIP auto-fill (physical)
  // ---------------------------------------------------------------------------

  const handleZipChange = useCallback(
    (raw: string): void => {
      onChange({ zip: raw });
      const clean = raw.replace(/\D/g, '');
      if (clean.length >= 5 && clean.slice(0, 5) !== lastZipRef.current) {
        const key = clean.slice(0, 5);
        lastZipRef.current = key;
        void usps.autoFillFromZip(key).then((r) => {
          if (!r) return;
          // Only fill what is empty (Collette Cruse life-ring report 2026-10-16;
          // same rule as RAM AddressChange): a city/state the officer typed or the
          // ID printed is never replaced by the ZIP lookup.
          const patch: Partial<RaoPerson> = {};
          if (!(cityRef.current ?? '').trim() && r.city) patch.city = titleCase(r.city);
          if (!(stateRef.current ?? '').trim() && r.state) patch.state = r.state.toUpperCase();
          if (Object.keys(patch).length > 0) onChange(patch);
        });
      }
    },
    [onChange, usps],
  );

  // ---------------------------------------------------------------------------
  // USPS full-address validate + accept-standardized
  // ---------------------------------------------------------------------------

  const verifyAddress = useCallback(async (): Promise<void> => {
    if (readOnly || !person.addr || !person.state) return;
    const body = {
      streetAddress: joinStreetLines(person.addr, person.addr2),
      state: person.state,
      ...(person.city ? { city: person.city } : {}),
      ...(person.zip ? { ZIPCode: person.zip.replace(/\D/g, '').slice(0, 5) } : {}),
    };
    await usps.validateAddress(body);
  }, [readOnly, person.addr, person.addr2, person.state, person.city, person.zip, usps]);

  const acceptStandardized = useCallback((): void => {
    if (readOnly || !usps.result) return;
    const canon = toCanonical(usps.result, stripZipPlus4);
    const patch: Partial<RaoPerson> = {};
    if (canon.streetAddress) {
      const next = standardizedStreet(canon, { addr: person.addr, addr2: person.addr2 });
      if (next.addr !== person.addr) patch.addr = next.addr;
      if (next.addr2 !== undefined && next.addr2 !== (person.addr2 ?? '')) patch.addr2 = next.addr2;
    }
    if (canon.city && canon.city !== person.city) patch.city = canon.city;
    if (canon.state && canon.state !== person.state) patch.state = canon.state;
    if (canon.zip && canon.zip !== person.zip) patch.zip = canon.zip;
    if (Object.keys(patch).length > 0) onChange(patch);
  }, [readOnly, usps.result, stripZipPlus4, person.addr, person.addr2, person.city, person.state, person.zip, onChange]);

  // ---------------------------------------------------------------------------
  // C1 (Bill 2026-06-03) — AUTO USPS verify for a touched/OCR'd address.
  //
  // The smoke test caught a mis-OCR'd street ("1994 16TH St Ouvehurst CA 95051"
  // vs the real "1984 16TH St Olivehurst CA 95961") sailing through verbatim —
  // because "Verify with USPS" was a manual button the officer never clicked.
  // We now auto-verify once an address the officer/OCR actually TOUCHED
  // (isChanged('addr')) settles with a street + state, debounced 700ms. The
  // standardized result + DPV badge + "Accept Standardized" surface
  // automatically so the correction can't be missed. We do NOT auto-apply (an
  // FSR-typed address is never silently overwritten) and we do NOT touch a
  // hydrated, untouched existing-CIF address (no isChanged → no call), so this
  // adds no USPS traffic for addresses already on file.
  const autoVerifiedSigRef = useRef<string>('');
  useEffect(() => {
    if (readOnly) return;
    if (!isChanged || !isChanged('addr')) return;
    const street = (person.addr ?? '').trim();
    const state = (person.state ?? '').trim();
    if (!street || !state) return;
    const sig = `${street}|${person.city ?? ''}|${state}|${person.zip ?? ''}`.toLowerCase();
    if (autoVerifiedSigRef.current === sig) return; // already auto-verified this exact address
    const t = setTimeout(() => {
      autoVerifiedSigRef.current = sig;
      void verifyAddress();
    }, 700);
    return () => clearTimeout(t);
  }, [person.addr, person.city, person.state, person.zip, readOnly, isChanged, verifyAddress]);

  // ---------------------------------------------------------------------------
  // Mailing toggle — persist via `extra.useMailingAddr`
  // ---------------------------------------------------------------------------

  const handleToggleMailing = useCallback(
    (next: boolean): void => {
      setUseMailingAddr(next);
      const prevExtra = (person.extra ?? {}) as Record<string, unknown>;
      onChange({
        extra: { ...prevExtra, useMailingAddr: next },
      });
    },
    [onChange, person.extra],
  );

  // ---------------------------------------------------------------------------
  // Derived USPS badge data
  // ---------------------------------------------------------------------------

  const dpvCode = (usps.result?.additionalInfo?.DPVConfirmation as string | undefined) ?? '';
  const dpvInfo = dpvCode ? DPV_LABELS[dpvCode] : null;
  const info = usps.result?.additionalInfo as
    | { vacant?: string; business?: string; DPVCMRA?: string }
    | undefined;
  const isVacant = info?.vacant === 'Y';
  const isBusinessAddr = info?.business === 'Y';
  const isCmra = info?.DPVCMRA === 'Y';

  const canAcceptStd = useMemo(() => {
    if (!usps.result) return false;
    // (Bill 2026-06-03) — never allow "Accept standardized" when the city is
    // missing. An incomplete address can't be corrected by USPS standardization;
    // the officer must re-scan or manually enter the city first.
    if (!person.city || person.city.trim() === '') return false;
    const canon = toCanonical(usps.result, stripZipPlus4);
    const next = canon.streetAddress ? standardizedStreet(canon, { addr: person.addr, addr2: person.addr2 }) : null;
    return (
      (next !== null && (next.addr !== person.addr || (next.addr2 !== undefined && next.addr2 !== (person.addr2 ?? '')))) ||
      (canon.city && canon.city !== person.city) ||
      (canon.zip && canon.zip !== person.zip)
    );
  }, [usps.result, stripZipPlus4, person.addr, person.addr2, person.city, person.zip]);

  // The suggested address as it would be saved (same rules as acceptStandardized).
  const suggestedLines = useMemo(() => {
    if (!usps.result) return null;
    const canon = toCanonical(usps.result, stripZipPlus4);
    const street = canon.streetAddress
      ? standardizedStreet(canon, { addr: person.addr, addr2: person.addr2 }).addr
      : joinStreetLines(person.addr, person.addr2);
    return { street, city: canon.city, state: canon.state, zip: canon.zip };
  }, [usps.result, stripZipPlus4, person.addr, person.addr2]);

  // ---------------------------------------------------------------------------
  // Render
  // ---------------------------------------------------------------------------

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
      {!readOnly && addressDonors.length > 0 && (
        <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap', fontSize: 13 }}>
          <span style={{ color: 'var(--ink-700)' }}>Same address as</span>
          {addressDonors.map((d) => (
            <Button
              key={d.id ?? d.fullName}
              variant="ghost"
              onClick={() => { if (d.id) copyAddressFrom(d.id); }}
              title={`Copy ${d.fullName || 'this person'}'s physical and mailing address`}
            >
              {d.fullName || [d.firstName, d.lastName].filter(Boolean).join(' ') || 'Person'}
            </Button>
          ))}
          {copiedFrom && (
            <span role="status" style={{ color: 'var(--pcb-green, #386732)' }}>
              Copied from {copiedFrom}. Check it before saving.
            </span>
          )}
        </div>
      )}
      {/* Physical address ---------------------------------------------------*/}
      <div>
        <div
          style={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            marginBottom: 8,
          }}
        >
          <span style={sectionHeaderStyle}>Physical Address</span>
          {!readOnly && (
            <Button
              variant="primary"
              size="sm"
              onClick={() => { void verifyAddress(); }}
              disabled={usps.verifying || !person.addr || !person.state}
            >
              {usps.verifying ? 'Verifying…' : 'Verify with USPS'}
            </Button>
          )}
        </div>

        <div style={{ marginBottom: 8 }}>
          <label style={labelStyle} htmlFor="rao-addr-street">Street address (include apartment / unit)</label>
          <input
            id="rao-addr-street"
            type="text"
            value={person.addr ?? ''}
            onChange={(e) => onChange({ addr: e.currentTarget.value })}
            readOnly={readOnly}
            style={changedStyle('addr')}
            placeholder="123 Main St, Apt 4"
            title="Street address"
          />
        </div>

        {/* An older record can still carry a separate street line 2 — show it so it is never
            hidden; "Move to street" joins it onto the street line (Bill 2026-10-06). */}
        {(person.addr2 ?? '').trim() !== '' && (
          <div style={{ marginBottom: 8 }}>
            <label style={labelStyle} htmlFor="rao-addr-street2">Street line 2</label>
            <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
              <input
                id="rao-addr-street2"
                type="text"
                value={person.addr2 ?? ''}
                onChange={(e) => onChange({ addr2: e.currentTarget.value })}
                readOnly={readOnly}
                style={changedStyle('addr2')}
                title="Street line 2"
              />
              {!readOnly && (
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={() => onChange({ addr: joinStreetLines(person.addr, person.addr2), addr2: '' })}
                >
                  Move to street
                </Button>
              )}
            </div>
          </div>
        )}

        <div style={{ display: 'grid', gridTemplateColumns: '2fr 1fr 1fr', gap: 8 }}>
          <div>
            <label style={labelStyle} htmlFor="rao-addr-city">City</label>
            <input
              id="rao-addr-city"
              type="text"
              value={person.city ?? ''}
              onChange={(e) => onChange({ city: e.currentTarget.value })}
              readOnly={readOnly}
              style={changedStyle('city')}
              title="City"
            />
          </div>
          <div>
            <label style={labelStyle} htmlFor="rao-addr-state">State</label>
            <select
              id="rao-addr-state"
              value={person.state ?? ''}
              onChange={(e) => onChange({ state: e.currentTarget.value })}
              disabled={readOnly}
              style={changedStyle('state')}
              title="State"
            >
              <option value="">--</option>
              {US_STATES.map((s) => (
                <option key={s.code} value={s.code}>{s.code}</option>
              ))}
            </select>
          </div>
          <div>
            <label style={labelStyle} htmlFor="rao-addr-zip">ZIP</label>
            <input
              id="rao-addr-zip"
              type="text"
              value={person.zip ?? ''}
              onChange={(e) => handleZipChange(e.currentTarget.value)}
              readOnly={readOnly}
              style={changedStyle('zip')}
              maxLength={10}
              placeholder="00000"
              title="ZIP code"
            />
          </div>
        </div>

        {/* USPS result badge strip ------------------------------------------*/}
        {usps.result && (
          <div
            style={{
              marginTop: 8,
              padding: '6px 10px',
              borderRadius: 6,
              fontSize: 12,
              display: 'flex',
              alignItems: 'center',
              gap: 8,
              flexWrap: 'wrap',
              background:
                dpvCode === 'Y' ? 'var(--ok-bg, #dff0d8)'
                : dpvCode === 'D' || dpvCode === 'S' ? 'var(--warn-bg, #fff3cd)'
                : 'var(--err-bg, #f2dede)',
            }}
          >
            {dpvInfo && (
              <span
                style={{
                  fontWeight: 600,
                  padding: '2px 8px',
                  borderRadius: 10,
                  background: dpvInfo.bg,
                  color: dpvInfo.color,
                }}
              >
                {dpvCode === 'Y' ? '✓' : dpvCode === 'N' ? '✗' : '⚠'} {dpvInfo.label}
              </span>
            )}
            {canAcceptStd && !readOnly && (
              <span style={{ color: 'var(--pcb-amber-text, #856404)', fontWeight: 600 }}>
                USPS suggests a correction — see below
              </span>
            )}
            {isVacant && (
              <span style={{ color: 'var(--pcb-red)', fontWeight: 600 }}>VACANT</span>
            )}
            {isBusinessAddr && (
              <span style={{ color: 'var(--pcb-amber-text, #856404)', fontWeight: 500 }}>
                Business Address
              </span>
            )}
            {isCmra && (
              <span style={{ color: 'var(--pcb-amber-text, #856404)', fontWeight: 500 }}>
                CMRA / Mail Drop
              </span>
            )}
          </div>
        )}
        {/* Line-by-line suggestion (Collette Cruse 2026-10-16): current -> suggested with the
            changed words highlighted, above the only button that applies it. */}
        {usps.result && canAcceptStd && !readOnly && suggestedLines && (
          <UspsSuggestionPanel
            current={{
              street: joinStreetLines(person.addr, person.addr2),
              city: person.city ?? '',
              state: person.state ?? '',
              zip: person.zip ?? '',
            }}
            suggested={suggestedLines}
            onAccept={acceptStandardized}
          />
        )}
        {usps.error && (
          <div style={{ fontSize: 11, marginTop: 4 }}>
            <div style={{ color: 'var(--pcb-red)', fontWeight: 600 }}>
              USPS validation failed: {usps.error}
            </div>
            {(!person.city || person.city.trim() === '') && (
              <div
                style={{
                  marginTop: 8,
                  padding: '8px 10px',
                  background: 'var(--err-bg, #fff1f1)',
                  borderLeft: '3px solid var(--pcb-red)',
                  color: 'var(--pcb-red)',
                  fontWeight: 600,
                  lineHeight: 1.4,
                }}
              >
                Address is incomplete — the city was not found during OCR extraction.
                Re-scan the document or manually enter the city to enable USPS verification.
              </div>
            )}
          </div>
        )}
      </div>

      {/* Mailing toggle -----------------------------------------------------*/}
      <div>
        <label
          style={{
            display: 'flex',
            alignItems: 'center',
            gap: 6,
            fontSize: 12,
            color: 'var(--ink-700)',
            cursor: readOnly ? 'not-allowed' : 'pointer',
          }}
        >
          <input
            type="checkbox"
            checked={useMailingAddr}
            onChange={(e) => handleToggleMailing(e.currentTarget.checked)}
            disabled={readOnly}
          />
          Mailing address differs from physical address
        </label>
      </div>

      {/* Mailing address (conditional) --------------------------------------*/}
      {useMailingAddr && (
        <div
          style={{
            padding: '10px 12px',
            background: 'var(--surface-subtle, #fafbfc)',
            borderRadius: 8,
            border: '1px solid var(--ink-100)',
          }}
        >
          <div style={sectionHeaderStyle}>Mailing Address</div>
          <div style={{ marginBottom: 8 }}>
            <label style={labelStyle} htmlFor="rao-mail-street">Street address</label>
            <input
              id="rao-mail-street"
              type="text"
              value={person.mailAddr ?? ''}
              onChange={(e) => onChange({ mailAddr: e.currentTarget.value })}
              readOnly={readOnly}
              style={changedStyle('mailAddr')}
              title="Mailing street address"
            />
          </div>
          <div style={{ display: 'grid', gridTemplateColumns: '2fr 1fr 1fr', gap: 8 }}>
            <div>
              <label style={labelStyle} htmlFor="rao-mail-city">City</label>
              <input
                id="rao-mail-city"
                type="text"
                value={person.mailCity ?? ''}
                onChange={(e) => onChange({ mailCity: e.currentTarget.value })}
                readOnly={readOnly}
                style={changedStyle('mailCity')}
                title="Mailing city"
              />
            </div>
            <div>
              <label style={labelStyle} htmlFor="rao-mail-state">State</label>
              <select
                id="rao-mail-state"
                value={person.mailState ?? ''}
                onChange={(e) => onChange({ mailState: e.currentTarget.value })}
                disabled={readOnly}
                style={changedStyle('mailState')}
                title="Mailing state"
              >
                <option value="">--</option>
                {US_STATES.map((s) => (
                  <option key={s.code} value={s.code}>{s.code}</option>
                ))}
              </select>
            </div>
            <div>
              <label style={labelStyle} htmlFor="rao-mail-zip">ZIP</label>
              <input
                id="rao-mail-zip"
                type="text"
                value={person.mailZip ?? ''}
                onChange={(e) => onChange({ mailZip: e.currentTarget.value })}
                readOnly={readOnly}
                style={changedStyle('mailZip')}
                maxLength={10}
                title="Mailing ZIP"
              />
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
